import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldCheck, ExternalLink, Loader2, LogOut, FileDown } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

const FREI = ['/agb', '/datenschutz', '/impressum'];
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return ''; } };

export const RechtstexteGate = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [offen, setOffen] = useState([]);
  const [ok, setOk] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pruefen = useCallback(() => {
    if (user?.role !== 'user') { setOffen([]); return; }
    api.get('/rechtstexte/status').then((r) => setOffen(r.data.offen || [])).catch(() => {});
  }, [user]);
  useEffect(() => { pruefen(); }, [pruefen]);
  useEffect(() => { window.addEventListener('rechtstexte-pflicht', pruefen); return () => window.removeEventListener('rechtstexte-pflicht', pruefen); }, [pruefen]);

  if (!offen.length || FREI.includes(pathname)) return null;
  const erst = offen.every((d) => d.erstzugang);
  const alle = offen.every((d) => ok[d.doc]);
  const bestaetigen = async () => {
    setBusy(true); setErr('');
    try { await api.post('/rechtstexte/bestaetigen', ok); setOffen([]); window.location.reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Bestätigung fehlgeschlagen'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-[#0f2236]/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rechtstexte-gate" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-lg shadow-2xl border-t-4 border-[#b8893d]">
        <div className="px-6 pt-6">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#b8893d]"><ShieldCheck className="w-4 h-4" /> Wichtige Information</div>
          <h2 className="font-serif text-2xl text-[#1B3A5C] mt-2" data-testid="rechtstexte-titel">{erst ? 'Willkommen in Ihrem Mandantenportal' : 'Unsere Rechtstexte haben sich geändert'}</h2>
          <p className="text-sm text-[#34536b] mt-2">{erst ? 'Bevor Sie das Portal nutzen, lesen und bestätigen Sie bitte einmalig unsere Rechtstexte.' : 'Bitte lesen und bestätigen Sie die aktualisierte Fassung. Danach können Sie Ihr Mandantenportal wie gewohnt weiter nutzen.'}</p>
        </div>
        <ul className="px-6 py-4 space-y-2">
          {offen.map((d) => (
            <li key={d.doc} className="border border-gray-200 px-4 py-3" data-testid={`rechtstexte-doc-${d.doc}`}>
              <p className="text-sm font-medium text-[#1B3A5C]">{d.titel}</p>
              <p className="text-[11px] text-[#7a8a99]">{d.stand ? `Stand ${d.stand} · ` : ''}gültig seit {fmt(d.erstellt_am)}</p>
              {d.aenderungshinweis && <p className="text-xs text-[#34536b] mt-1">{d.aenderungshinweis}</p>}
              <div className="flex flex-wrap gap-3 mt-2 text-xs">
                <a href={d.pfad} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#b8893d] hover:text-[#1B3A5C]" data-testid={`rechtstexte-link-${d.doc}`}><ExternalLink className="w-3 h-3" /> Vollständigen Text lesen</a>
                {d.pdf && <a href={`${process.env.REACT_APP_BACKEND_URL}${d.pdf}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#b8893d] hover:text-[#1B3A5C]" data-testid={`rechtstexte-pdf-${d.doc}`}><FileDown className="w-3 h-3" /> PDF</a>}
              </div>
              <label className="flex items-start gap-2 mt-3 pt-2 border-t border-gray-100 text-sm text-[#1B3A5C] cursor-pointer">
                <input type="checkbox" checked={!!ok[d.doc]} onChange={(e) => setOk((o) => ({ ...o, [d.doc]: e.target.checked }))} className="mt-1 accent-[#b8893d]" data-testid={`rechtstexte-checkbox-${d.doc}`} />
                <span>{d.doc === 'agb' ? 'Ich habe die Allgemeinen Mandatsbedingungen (AGB) gelesen und akzeptiere sie.' : 'Ich habe die Datenschutzerklärung gelesen und zur Kenntnis genommen.'}</span>
              </label>
            </li>
          ))}
        </ul>
        {err && <p className="mx-6 mb-2 px-2 py-1 text-xs text-red-700 bg-red-50" data-testid="rechtstexte-error">{err}</p>}
        <div className="flex items-center justify-between gap-2 px-6 py-4 bg-[#f5f8fa]">
          <button type="button" onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-[#7a8a99] hover:text-[#1B3A5C]" data-testid="rechtstexte-logout"><LogOut className="w-3.5 h-3.5" /> Abmelden</button>
          <button type="button" onClick={bestaetigen} disabled={!alle || busy} data-testid="rechtstexte-bestaetigen"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 text-[11px] tracking-[0.2em] bg-[#1B3A5C] text-white hover:bg-[#b8893d] transition-colors disabled:opacity-40">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} BESTÄTIGEN & FORTFAHREN
          </button>
        </div>
      </div>
    </div>
  );
};
